import {
  createElement,
  type ComponentType,
  type RefObject,
  useContext,
  useRef,
} from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { themeColors, themeSizes } from '../../../../styles/globalStyleVars';
import styled, { css } from 'styled-components';
import type {
  IntroductionInterface,
  SettingsInterface,
} from '../../../../interfaces/IntroductionInterface';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { DownloadAppQRWithText } from '../../DownloadAppQRWithText';
import { TypographyStyled } from '../../../Styled/Typography';
import { Button, IconButton, Link } from '@cfa/react-core';
import { UserContext } from '../../../../user/UserContext';
import type { UserContextType } from '../../../../@types/user';
import { useNavigate } from 'react-router-dom';

// Type definition for Slider component props
type SliderProps = SettingsInterface & {
  ref?: RefObject<Slider | null>;
};

// react-slick is CJS-only. Because package.json is "type": "module", Vite 8
// builds apply Node-style interop and the default import resolves to the
// module.exports object ({ default: Slider }) instead of the component.
const SliderComponent =
  (Slider as unknown as { default?: typeof Slider }).default ?? Slider;

// Min width where the video's natural height can overflow the fixed-height modal (below lg)
const VIDEO_HEIGHT_CAP_MIN_WIDTH = '480px';

// Per design, the video is capped at 450px wide on tablet
const VIDEO_MAX_WIDTH = '450px';

// Fit the video in the modal: 90vh modal height minus ~420px of header, description, and nav.
// Never shrink below 240px — in very short windows the modal scrolls instead
const VIDEO_MAX_HEIGHT = 'max(240px, calc(90vh - 420px))';

const SliderContainerDiv = styled.div<{
  $isLgAndUp: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  .slide-wrapper {
    align-items: ${({ $isLgAndUp }) => ($isLgAndUp ? 'center' : 'start')};
    display: flex !important;
    flex-direction: ${({ $isLgAndUp }) =>
      $isLgAndUp ? 'row' : 'column-reverse'};
    padding: 10px;
  }
`;

const SliderContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const SlideListItem = styled.div`
  padding-bottom: ${themeSizes.sm};

  &:last-child {
    padding-bottom: 0;
  }
`;

const mediaStyles = {
  height: 'auto',
  width: '100%',
  margin: '0 auto',
  display: 'block',
  'border-radius': `${themeSizes.sm}`,
};

const SlideImage = styled.img`
  ${{ ...mediaStyles }}
`;

const SlideVideo = styled.video<{ $isLgAndUp: boolean }>`
  ${{ ...mediaStyles }}
  ${({ $isLgAndUp }) =>
    $isLgAndUp
      ? css`
          @media screen and (min-width: 676px) {
            min-width: 450px;
          }
        `
      : css`
          @media screen and (min-width: ${VIDEO_HEIGHT_CAP_MIN_WIDTH}) {
            max-width: ${VIDEO_MAX_WIDTH};
            max-height: ${VIDEO_MAX_HEIGHT};
            object-fit: contain;
          }
        `}
`;

const SlideDescriptionWrapper = styled.div<{ $hasButton: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ $hasButton }) =>
    $hasButton ? 'flex-start' : 'center'};
  height: 100%;
`;

type IntroductionCarouselSlidesProps = {
  sliderRef: RefObject<Slider | null>;
  settings: SettingsInterface;
  introductionSlides: IntroductionInterface[];
  isLgAndUp: boolean;
  activeFlags: string[];
  isMobileOS: string | undefined;
  onClose: () => void;
};

const IntroductionCarouselSlides = ({
  sliderRef,
  settings,
  introductionSlides,
  isLgAndUp,
  activeFlags,
  isMobileOS,
  onClose,
}: IntroductionCarouselSlidesProps) => {
  const navigate = useNavigate();

  const handleButtonClick = (href: string) => {
    onClose();
    const [path, hash] = href.split('#');
    navigate(path);
    if (hash) {
      setTimeout(() => {
        document
          .getElementById(hash)
          ?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 1500);
    }
  };

  return createElement(
    SliderComponent as ComponentType<SliderProps>,
    {
      ref: sliderRef,
      ...settings,
    },
    introductionSlides.map(slide => (
      <div
        key={slide.id}
        className='slide-wrapper'
        data-testid='intro-slide-item'
      >
        <div
          style={{
            textAlign: 'left',
            paddingRight: themeSizes.md,
          }}
        >
          {slide.title && (
            <TypographyStyled
              text={isLgAndUp ? slide.title : slide?.titleMobile}
              color={themeColors.colorGray_7}
              variant={isLgAndUp ? 'h4' : 'body1'}
              fontWeight={isLgAndUp ? 'bold' : 'medium'}
            />
          )}
          {slide.qrCode && (isMobileOS || isLgAndUp) && (
            <DownloadAppQRWithText
              text='Download the Support App to scan your equipment asset tag and find it
          faster.'
              isLgAndUp={isLgAndUp}
            />
          )}
          {slide.description ? (
            // Description + optional button layout
            <SlideDescriptionWrapper $hasButton={!!slide.button}>
              <TypographyStyled
                text={slide.description}
                color={themeColors.colorGray_8}
                fontWeight='medium'
              />
              {slide.button && (
                <div style={{ marginTop: themeSizes.md }}>
                  <Button
                    color='primary'
                    size='md'
                    onPress={() =>
                      slide.button?.href && handleButtonClick(slide.button.href)
                    }
                  >
                    {slide.button.label}
                  </Button>
                </div>
              )}
            </SlideDescriptionWrapper>
          ) : (
            // Legacy bullet items layout
            slide.bulletItems &&
            slide.bulletItemsMobile &&
            (isLgAndUp ? slide.bulletItems : slide.bulletItemsMobile).map(
              (bulletItem, index) => {
                if (
                  bulletItem.featureFlag &&
                  !activeFlags.includes(bulletItem.featureFlag)
                )
                  return;
                return (
                  <div
                    style={{
                      paddingLeft: themeSizes.lg,
                      marginTop: themeSizes.md,
                    }}
                    key={index}
                  >
                    <SlideListItem>
                      {bulletItem.link ? (
                        <Link
                          inline
                          href={bulletItem.link}
                          target='_blank'
                          rel='noreferrer'
                          style={{
                            color: themeColors.secondaryBlue,
                            fontWeight: '500',
                          }}
                        >
                          {bulletItem.label}
                        </Link>
                      ) : (
                        <TypographyStyled
                          text={bulletItem.label}
                          color={themeColors.colorGray_8}
                          fontWeight='medium'
                        />
                      )}
                    </SlideListItem>
                  </div>
                );
              }
            )
          )}
        </div>
        <div
          style={
            isLgAndUp
              ? { maxWidth: '450px', flexShrink: 0, marginLeft: themeSizes.md }
              : { marginBottom: '28px', width: '100%' }
          }
        >
          {slide.image && <SlideImage src={slide.image} alt={slide.alt} />}
          {slide.video && (
            <SlideVideo controls preload='metadata' $isLgAndUp={isLgAndUp}>
              <source src={`${slide.video}#t=0.001`} type='video/mp4' />
            </SlideVideo>
          )}
        </div>
      </div>
    ))
  );
};

const SlideDots = styled.div`
  display: flex;
  align-items: center;
  gap: ${themeSizes.sm};
`;

const SlideDot = styled.button<{ $active: boolean }>`
  width: ${themeSizes.sm};
  height: ${themeSizes.sm};
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background-color: ${({ $active }) =>
    $active ? themeColors.secondaryBlue : themeColors.colorGray_2};

  &:hover {
    background-color: ${themeColors.secondaryBlueHover};
  }
`;

const SlideNavigation = styled.div<{ $isLgAndUp: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ $isLgAndUp }) =>
    $isLgAndUp ? themeSizes.lg : themeSizes.sm};
  flex-shrink: 0;
`;

type PreviousNextButtonContainerProps = {
  slideIndex: number;
  introductionSlides: Array<IntroductionInterface>;
  onClickNext: () => void;
  onClickPrev: () => void;
  onDotClick: (index: number) => void;
  isLgAndUp: boolean;
};

const PreviousNextButtonContainer = ({
  slideIndex,
  introductionSlides,
  onClickNext,
  onClickPrev,
  onDotClick,
  isLgAndUp,
}: PreviousNextButtonContainerProps) => (
  <SlideNavigation $isLgAndUp={isLgAndUp}>
    <IconButton
      aria-label='Previous slide'
      onPress={onClickPrev}
      isDisabled={slideIndex === 0}
    >
      <IconChevronLeft />
    </IconButton>
    <SlideDots role='tablist' aria-label='Slide navigation'>
      {introductionSlides.map((_, index) => (
        <SlideDot
          key={index}
          role='tab'
          aria-label={`Go to slide ${index + 1}`}
          aria-selected={index === slideIndex}
          $active={index === slideIndex}
          onClick={() => onDotClick(index)}
        />
      ))}
    </SlideDots>
    <IconButton
      aria-label='Next slide'
      onPress={onClickNext}
      isDisabled={slideIndex + 1 === introductionSlides.length}
    >
      <IconChevronRight />
    </IconButton>
  </SlideNavigation>
);

type IntroductionModalBodyProps = {
  isLgAndUp: boolean;
  setSlideIndex: (slideNumber: number) => void;
  slideIndex: number;
  introductionSlides: Array<IntroductionInterface>;
  activeFlags: string[];
  onClose: () => void;
};

const IntroductionModalBody = ({
  isLgAndUp,
  setSlideIndex,
  slideIndex,
  introductionSlides,
  activeFlags,
  onClose,
}: IntroductionModalBodyProps) => {
  const sliderRef = useRef<Slider>(null);
  const settings = {
    dots: false,
    infinite: false,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
    //biome-ignore lint/correctness/noUnusedFunctionParameters: maintain library params
    beforeChange: (current: number, next: number) => setSlideIndex(next),
  };

  const { userState } = useContext(UserContext) as UserContextType;

  return (
    <SliderContainerDiv className='slider-container' $isLgAndUp={isLgAndUp}>
      <SliderContent>
        <IntroductionCarouselSlides
          sliderRef={sliderRef}
          settings={{ ...settings }}
          introductionSlides={introductionSlides}
          isLgAndUp={isLgAndUp}
          activeFlags={activeFlags}
          isMobileOS={userState.mobileOS}
          onClose={onClose}
        />
      </SliderContent>
      <PreviousNextButtonContainer
        slideIndex={slideIndex}
        introductionSlides={introductionSlides}
        onClickNext={() => sliderRef.current?.slickNext()}
        onClickPrev={() => sliderRef.current?.slickPrev()}
        onDotClick={index => sliderRef.current?.slickGoTo(index)}
        isLgAndUp={isLgAndUp}
      />
    </SliderContainerDiv>
  );
};

export default IntroductionModalBody;
